import { useQuery } from '@tanstack/react-query';
import { Loader2, SendHorizontal, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { assistantApi, assistantKeys } from '@/features/assistant/assistant-api';
import { AssistantMarkdown } from '@/features/assistant/assistant-markdown';
import { useAssistantStore } from '@/features/assistant/assistant-store';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { errorMessage } from '@/lib/error-message';
import { cn } from '@/lib/utils';

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** Botão do cabeçalho: só aparece quando a API informa que o assistente está configurado. */
export function AssistantButton() {
  const status = useQuery({
    queryKey: assistantKeys.status,
    queryFn: assistantApi.status,
    staleTime: 10 * 60 * 1000,
  });
  const setOpen = useAssistantStore((s) => s.setOpen);
  if (!status.data?.enabled) return null;
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Assistente de IA"
            onClick={() => setOpen(true)}
          >
            <Sparkles />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Assistente de IA</TooltipContent>
      </Tooltip>
      <AssistantPanel suggestions={status.data.suggestions} model={status.data.model} />
    </>
  );
}

function AssistantPanel({ suggestions, model }: { suggestions: string[]; model: string | null }) {
  const { open, entries, pending, setOpen, append, setPending, clear } = useAssistantStore();
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [entries.length, pending]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || pending) return;
    const history = entries
      .filter((e) => !e.error)
      .map((e) => ({ role: e.role, content: e.content }));
    append({ id: newId(), role: 'user', content });
    setDraft('');
    setPending(true);
    try {
      const res = await assistantApi.chat([...history, { role: 'user', content }]);
      append({ id: newId(), role: 'assistant', content: res.reply, toolsUsed: res.toolsUsed });
    } catch (error) {
      append({
        id: newId(),
        role: 'assistant',
        content: errorMessage(error, 'Não foi possível obter a resposta do assistente.'),
        error: true,
      });
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <div className="flex items-center gap-2 border-b px-4 py-3 pr-12">
          <span className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="size-4" />
          </span>
          <div className="min-w-0">
            <SheetTitle className="text-sm">Assistente de IA</SheetTitle>
            <SheetDescription className="truncate text-xs">
              Responde com base nos dados que você tem permissão de ver
              {model ? ` · ${model}` : ''}
            </SheetDescription>
          </div>
          {entries.length > 0 ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              aria-label="Limpar conversa"
              onClick={clear}
            >
              <Trash2 />
            </Button>
          ) : null}
        </div>

        <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {entries.length === 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pergunte sobre caixa, títulos, impostos, estoque, produção, vendas, compras, alertas
                ou relatórios. Eu consulto a plataforma e respondo com os números reais.
              </p>
              <div className="flex flex-col gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-accent"
                    onClick={() => void send(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {entries.map((e) => (
            <div
              key={e.id}
              className={cn('flex', e.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[90%] rounded-lg px-3 py-2',
                  e.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : e.error
                      ? 'border border-destructive/40 bg-destructive/5 text-destructive'
                      : 'bg-muted',
                )}
              >
                {e.role === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{e.content}</p>
                ) : (
                  <AssistantMarkdown text={e.content} />
                )}
                {e.toolsUsed?.length ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {e.toolsUsed.map((t, i) => (
                      <Badge key={`${t.name}-${i}`} variant="outline" className="text-[10px]">
                        Consultou: {t.label}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {pending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Consultando a plataforma…
            </div>
          ) : null}
        </div>

        <form
          className="border-t p-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            void send(draft);
          }}
        >
          <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              rows={2}
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' && !ev.shiftKey) {
                  ev.preventDefault();
                  void send(draft);
                }
              }}
              placeholder="Pergunte algo sobre a empresa… (Enter envia, Shift+Enter quebra linha)"
              aria-label="Mensagem para o assistente"
              className="min-h-0 resize-none"
              disabled={pending}
            />
            <Button
              type="submit"
              size="icon"
              aria-label="Enviar"
              disabled={pending || !draft.trim()}
            >
              <SendHorizontal />
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            O assistente apenas consulta dados e orienta; não altera registros. Confira valores
            antes de decidir.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
